import { getLatestSchemaVersion } from './bitDataConverter/getLatestSchemaVersion';
import { toBitwiseData } from './bitDataConverter';
import { isRMDT, isGFT, isULA, isRMIFIProduct, isRMGridFlex, isAscender, isSolarMount, isNxtHorizon, isSMTiltPR, isRM10orRM10Evo, isEcoFoot2Plus, isRM5, isRmGridflex10D, isRmGridflex5Rwdi, isNxtTilt } from '__common/constants/products';
import store from '__common/store';
import { isASCE716or722 } from '__common/constants/buildingCodes';
import { getMaxSetBack } from '__editor/panelsEditor/components/roofZones/utils/setBackDistanceCollisionDetections';
import { metersToFeets } from '__common/calculations/metersToFeets';

interface saveDataDetails {
  x: number;
  y: number;
  groupId: number;
  bitData: number;
  siblingId?: number;
  siblingSide?: number;
  panelEWPosition?: number;
  pairId?: number;
  panelConfig?: number;
  roofZoneId?: number;
  schemaVersion?: number;
  panelNSPosition?: number;
  tableHeight?: number;
  attached?: number;
  panelSetback?: number;
  manualAttachments?: (0|1)[];
  skewAffected?: number;
  manualExposure?: 'E' | 'NE';
}

export function parseSaveData(data: panelsSaveData, productId: number): saveDataDetails {
  const { app: { isUbuilderLite } } = store.getState() as appState;
  let siblingId: number;
  let siblingSide: number;
  let pairId: number;
  let panelConfig: number;
  let roofZoneId: number;
  const [x, y, bitData, groupId] = data;

  if (isRMDT(productId)) {
    siblingId = data[4];
    siblingSide = data[5];

    return {
      x,
      y,
      bitData,
      groupId,
      siblingId,
      siblingSide,
      schemaVersion: data[6],
    };
  }
  if (isAscender(productId)) {
    siblingId = data[4];
    siblingSide = data[5];
    pairId = data[7];
    roofZoneId = data[8];
    panelConfig = data[9];
    const panelEWPosition = data[6];
    return {
      x,
      y,
      bitData, 
      groupId,
      siblingId,
      siblingSide,
      panelEWPosition,
      pairId,
      roofZoneId,
      panelConfig,
      schemaVersion: data[10],
    };
  }

  if (isGFT(productId)) {
    siblingId = data[4];
    siblingSide = data[5];
    const panelEWPosition = data[6];
    return {
      x,
      y,
      bitData,
      groupId,
      siblingId,
      siblingSide,
      panelEWPosition,
      schemaVersion: data[7],
    };
  }

  if (isULA(productId)) {
    siblingId = data[4];
    siblingSide = data[5];
    const panelEWPosition = data[6];
    const panelNSPosition = data[8];
    const tableHeight = data[9];
    return {
      x,
      y,
      bitData,
      groupId,
      siblingId,
      siblingSide,
      panelEWPosition,
      schemaVersion: data[7],
      panelNSPosition,
      tableHeight,
    };
  }

  if (isRMGridFlex(productId)) {
    const schemaVersion = data[4];
    let panelSetback = 0;
    let attached = 0, skewAffected = 0;
    if (schemaVersion === 3) attached = data[5];
    if(data.length > 6){
      panelSetback = data[6];
    }
    if(data.length > 7){
      skewAffected = data[7];
    }
    if (!panelSetback && isUbuilderLite) {
      panelSetback = getFallbackSetback(productId);
    }
    return {
      x,
      y,
      bitData,
      groupId,
      schemaVersion,
      attached, 
      panelSetback,
      skewAffected,
    };
  }

  if ((isSolarMount(productId) || isNxtHorizon(productId) || isSMTiltPR(productId) || isNxtTilt(productId)) && data.length > 5) {
    return {
      x,
      y,
      bitData,
      groupId,
      roofZoneId: data[4],
      manualExposure: data.length > 6 ? data[5] : undefined,
      schemaVersion: data.length > 6 ? data[6] : data[5],
    };
  }

  if (isRM10orRM10Evo(productId) || isEcoFoot2Plus(productId) || isRM5(productId)) {
    let panelSetback = 0, schemaVersion = data[4], skewAffected = 0, manualExposure = undefined;
    let manualAttachments = undefined;
    if (data.length > 5) {
      panelSetback = data[4];
      manualAttachments = data[5];
      schemaVersion = data.pop();
    }
    if (data.length > 7) {
      manualExposure = data[7];
    }
    if(data.length > 6) {
      skewAffected = data[6];
    }
    if (!panelSetback && isUbuilderLite) {
      panelSetback = getFallbackSetback(productId);
    }
    return {
      x,
      y,
      bitData,
      groupId,
      panelSetback,
      schemaVersion,
      manualAttachments,
      skewAffected,
      manualExposure,
    };
  }
  if ( isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) {
    let panelSetback = 0, schemaVersion = data[4];
    let attached=0, skewAffected = 0, manualExposure = undefined;
    if (schemaVersion === 3) attached = data[7];
    if(data.length > 5){
      panelSetback = data[4];
      schemaVersion = data[5];
    }
    if(data.length > 7){;
      skewAffected = data[7];;
    }
    if (!panelSetback && isUbuilderLite) {
      panelSetback = getFallbackSetback(productId);
    }
    if(data.length > 8){
      manualExposure = data[8];
    }
    return {
      x,
      y,
      bitData, 
      groupId,
      panelSetback,
      schemaVersion,
      attached,
      skewAffected,
    };
  }
  return {
    x,
    y,
    bitData,
    groupId,
    schemaVersion: data[4],
  };
}


function getFallbackSetback(productId: number): number {
  const state = store.getState() as appState;
  const projectConfiguration = state?.projectConfiguration;
  if (!projectConfiguration) return 3;
  const projectEnvConfig = projectConfiguration.projectEnvConfig;
  if (!projectEnvConfig) return 3;
  const inputUnit = projectConfiguration.inputUnit ?? projectEnvConfig.input_unit ?? 1;
  const projectVersion = projectConfiguration.projectVersion;
  const maxSetbackInMeters = getMaxSetBack(projectEnvConfig, inputUnit, productId, projectVersion);
  if (isNaN(maxSetbackInMeters) || maxSetbackInMeters === null || maxSetbackInMeters === undefined || maxSetbackInMeters === 0) {
    return projectEnvConfig.setback_distance ?? 3;
  }
  const setbackFeet = Math.round(metersToFeets(maxSetbackInMeters) * 100) / 100;
  return setbackFeet || projectEnvConfig.setback_distance || 3;
}


export function convertToSaveData(
  panel: panelInState,
  productId: number,
  buildingCode: number,
  roofPitch: number,
  baysInfo?: any,
): panelsSaveData | panelsSaveDataRM10Evo {
  const { projectConfiguration: { projectEnvConfig: { allow_manual_attachments, is_aurora_project, is_os_project, building_code } }, app: { isUbuilderLite } } = store.getState() as appState;
  const schemaVersion = getLatestSchemaVersion(productId, buildingCode);
  const bitData = toBitwiseData(panel, buildingCode, productId, schemaVersion, roofPitch);
  const x = parseFloat(panel.x.toFixed(4));
  const y = parseFloat(panel.y.toFixed(4));
  const groupId = panel.groupId;
  const baysData = baysInfo && Object.values(baysInfo).map((bay:any) => bay.attached).map(attached=> attached?1:0); 
  if (isRMDT(productId)) {
    const siblingId = panel.siblingId;
    const siblingSide = panel.siblingSide;
    return [x, y, bitData, groupId, siblingId, siblingSide, schemaVersion];
  }
  if (isAscender(productId)) {
    const { siblingId, siblingSide, panelEWPosition, pairId, panelConfig } = panel;
    const roofZoneId = panel.roofZone;
    return [x, y, bitData, groupId, siblingId, siblingSide, panelEWPosition, pairId, roofZoneId, panelConfig, schemaVersion];
  }
  if (isGFT(productId)) {
    const { siblingId, siblingSide, panelEWPosition } = panel;
    return [x, y, bitData, groupId, siblingId, siblingSide, panelEWPosition, schemaVersion];
  }
  if (isULA(productId)) {
    const { siblingId, siblingSide, panelEWPosition, panelNSPosition, tableHeight } = panel;
    return [x, y, bitData, groupId, siblingId, siblingSide, panelEWPosition, schemaVersion, panelNSPosition, tableHeight];
  }
  if (isRMIFIProduct(productId)) {
    return [panel.x, panel.y, panel.obstructionType, groupId, schemaVersion, Number(panel.attached), panel.panelSetback, panel.skewAffected ?? 0];
  }
  if (isRM10orRM10Evo(productId) || isEcoFoot2Plus(productId) || isRM5(productId)){
    const manualAttachmentsSelection: (0 | 1)[] = allow_manual_attachments ? baysData??panel.manualAttachments : [0,0,0,0];
    const setback = (panel.panelSetback || !isUbuilderLite) ? panel.panelSetback : getFallbackSetback(productId);
    return [panel.x, panel.y, bitData, groupId, setback, manualAttachmentsSelection, panel.skewAffected ?? 0, panel.manualExposure, schemaVersion];
  }

  if (isSolarMount(productId) || isNxtHorizon(productId) || isSMTiltPR(productId) || isNxtTilt(productId)) {
    let roofZoneId = panel.roofZone;
    if ((is_aurora_project || is_os_project) && isASCE716or722(buildingCode)){
      roofZoneId = 1;
    }
    return [panel.x, panel.y, bitData, groupId, roofZoneId, panel.manualExposure, schemaVersion];
  }

  if (isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)){
    const setback = (panel.panelSetback || !isUbuilderLite) ? panel.panelSetback : getFallbackSetback(productId);
    return [panel.x, panel.y, bitData, groupId, setback, schemaVersion, Number(panel.attached), panel.skewAffected ?? 0, panel.manualExposure];
  }

  return [panel.x, panel.y, bitData, groupId, schemaVersion];
}
